// CompareExecutions.tsx — session 6 T3: "Compare two openings". The executions of ONE approved storyboard as animatics,
// side by side, at gate_compare — before any clip is made. One component for the customer and the operator: what each
// card says and allows is `compareCards` (pure), so the two screens cannot disagree. Costs only when asked (the operator);
// a failure's technical reason only to the operator.

import { w, useWords } from "@/lib/words.ts";   // (B107) the customer's words (the operator's failure reason stays as it is)

export interface CompareExecution {
  index: number; axes: string[]; status: "pending" | "ready" | "failed"; animaticUrl: string | null;
  failureReason?: string | null; costUsdCents?: number; spendSource?: string;
}
export interface CompareData { round?: number | null; executions: CompareExecution[]; awaitingPick: boolean }

export interface CompareCard { index: number; label: string; playable: boolean; chooseable: boolean; note: string | null; costUsdCents: number | null }

const AXIS_NOUN: Record<string, () => string> = { hook: () => w("page.compare.axis.opening"), caption_style: () => w("page.compare.axis.caption_style") };

/** Pure: each execution as a card — its label, whether it can play and be chosen, and what to say about it. */
export function compareCards(d: CompareData, o: { operator: boolean }): CompareCard[] {
  return d.executions.map((e) => {
    const noun = e.axes.length ? e.axes.map((a) => AXIS_NOUN[a]?.() ?? a).join(" + ") : null;
    const label = e.index === 0 ? w("page.compare.as_approved") : w("page.compare.version", { axis: noun ?? w("page.compare.axis.version"), n: e.index + 1 });
    const playable = e.status === "ready" && !!e.animaticUrl;
    const note = e.status === "failed" ? (o.operator && e.failureReason ? `Couldn't be made: ${e.failureReason}` : w("page.compare.not_made"))
      : e.status === "ready" && !e.animaticUrl ? w("page.compare.preparing") : e.status === "pending" ? w("page.compare.being_made") : null;
    return { index: e.index, label, playable, chooseable: d.awaitingPick && playable, note,
      costUsdCents: o.operator ? (e.costUsdCents ?? 0) : null };
  });
}

export function CompareExecutions(p: { data: CompareData; operator: boolean; busy: boolean;
    onChoose: (index: number) => void; onReject: () => void }) {
  useWords();   // (B107) the store's words wherever it shows (the video page, the Run Inspector)
  const cards = compareCards(p.data, { operator: p.operator });
  const anyChooseable = cards.some((c) => c.chooseable);
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(cards.length, 3) || 1}, minmax(0, 1fr))`, gap: 16 }}>
        {cards.map((c) => (
          <div key={c.index} style={{ border: "1px solid var(--tf-rule, #e5e7eb)", borderRadius: 8, padding: 12, opacity: c.note && !c.playable ? 0.6 : 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{c.label}</div>
            {c.playable
              ? <video src={p.data.executions.find((e) => e.index === c.index)!.animaticUrl!} controls playsInline preload="metadata"
                  style={{ width: "100%", borderRadius: 6, background: "#000" }} />
              : <div style={{ aspectRatio: "9 / 16", background: "#f3f4f6", borderRadius: 6 }} />}
            {c.note && <p style={{ fontSize: 12, color: "#6b7280", margin: "8px 0 0" }}>{c.note}</p>}
            {c.costUsdCents !== null && <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>${(c.costUsdCents / 100).toFixed(2)}</p>}
            <button disabled={!c.chooseable || p.busy} onClick={() => p.onChoose(c.index)}
              style={{ marginTop: 10, width: "100%", padding: "6px 10px", borderRadius: 6, border: 0,
                background: c.chooseable ? "#111827" : "#e5e7eb", color: c.chooseable ? "#fff" : "#9ca3af", cursor: c.chooseable ? "pointer" : "default" }}>
              {w("page.direction.choose")}
            </button>
          </div>))}
      </div>
      {p.data.awaitingPick && (
        <button disabled={p.busy} onClick={p.onReject}
          style={{ marginTop: 14, background: "transparent", border: 0, color: "#2563eb", cursor: "pointer", padding: 0 }}>
          {w("page.compare.none_of_these")}
        </button>)}
      {!anyChooseable && p.data.awaitingPick && <p style={{ fontSize: 12, color: "#6b7280" }}>{w("page.compare.still_preparing")}</p>}
    </div>
  );
}
